import { memo, useCallback, useMemo } from 'react';
import { MathUtils } from 'three';
import { t } from 'i18next';
import { LogicalSpace, Point, Polygon } from '@thive/fddm-types';

import { Dimensions } from '@/modules/common/components/Dimensions';
import { Accordion } from '@/modules/common/components/Accordion';
import { NumUtils } from '@/modules/common/utils';
import { clampAngle180 } from '@/modules/common/helpers/math';
import { FddmServiceFactory } from '@/modules/canvas/services/Floorplan/FddmServiceFactory';
import { useAppStore } from '@/modules/app/store/useAppStore';
import { RectangleUtils } from '@/modules/floorplanDesign/helpers/RectangleUtils';

type Props = {
  logicalSpace: LogicalSpace;
  disabled?: boolean;
};

const pointsToPolygon = (points: Point[]): Polygon | null => {
  const [first, second, third, ...rest] = points;

  if (!first || !second || !third) {
    return null;
  }

  return {
    points: [first, second, third, ...rest],
  };
};

function LogicalSpaceDimensionsComponent({ logicalSpace, disabled }: Props) {
  const mode = useAppStore((state) => state.mode);
  const fddmSvc = useMemo(() => FddmServiceFactory.getInstance().createFddmService(), []);

  const isDisabled = useMemo(() => {
    if (disabled !== undefined) return disabled;

    return mode !== 'EDITABLE';
  }, [disabled, mode]);

  const dimensions = useMemo(() => {
    const rectangle = RectangleUtils.polygonToRectangle(logicalSpace.polygon);
    if (!rectangle) return null;

    const { centerX, centerY, width, length, angle } = rectangle;
    return {
      centerX,
      centerY,
      length,
      width,
      angle: MathUtils.radToDeg(angle),
    };
  }, [logicalSpace.polygon]);

  const updatePolygon = useCallback(
    (
      updates: Partial<{
        centerX: number;
        centerY: number;
        width: number;
        length: number;
        angle: number;
      }>,
    ) => {
      if (isDisabled) return;

      const rectangle = RectangleUtils.polygonToRectangle(logicalSpace.polygon);
      if (!rectangle) return;

      const newRectangle = { ...rectangle, ...updates };
      const points = RectangleUtils.rectangleToPolygon(newRectangle).map((point) => ({
        x: point.x,
        y: point.y,
      }));

      const polygon = pointsToPolygon(points);
      if (!polygon) return;

      fddmSvc.updateLogicalSpace({ ...logicalSpace, polygon });
    },
    [fddmSvc, isDisabled, logicalSpace],
  );

  const onXChange = useCallback(
    (newValue: number) => {
      if (isDisabled) return;

      const newX = NumUtils.mmToMetersRoundedToMm(newValue);
      const rectangle = RectangleUtils.polygonToRectangle(logicalSpace.polygon);
      if (!rectangle) return;

      updatePolygon({ centerX: newX, centerY: rectangle.centerY });
    },
    [isDisabled, logicalSpace.polygon, updatePolygon],
  );

  const onYChange = useCallback(
    (newValue: number) => {
      if (isDisabled) return;

      const newY = NumUtils.mmToMetersRoundedToMm(newValue);
      const rectangle = RectangleUtils.polygonToRectangle(logicalSpace.polygon);
      if (!rectangle) return;

      updatePolygon({ centerX: rectangle.centerX, centerY: newY });
    },
    [isDisabled, logicalSpace.polygon, updatePolygon],
  );

  const onLengthChange = useCallback(
    (newValue: number) => {
      if (isDisabled) return;

      updatePolygon({ length: NumUtils.mmToMetersRoundedToMm(newValue) });
    },
    [isDisabled, updatePolygon],
  );

  const onWidthChange = useCallback(
    (newValue: number) => {
      if (isDisabled) return;

      updatePolygon({ width: NumUtils.mmToMetersRoundedToMm(newValue) });
    },
    [isDisabled, updatePolygon],
  );

  const onAngleChange = useCallback(
    (newValue: number) => {
      if (isDisabled) return;
      updatePolygon({ angle: -MathUtils.degToRad(clampAngle180(newValue)) });
    },
    [isDisabled, updatePolygon],
  );

  if (!dimensions) return null;

  return (
    <Accordion
      title={t<string>('interface:properties.dimensions.label', 'Dimensions')}
      unmountOnExit
      defaultExpanded
      sx={{ px: 2 }}
    >
      <Dimensions
        key={logicalSpace.id}
        disabled={isDisabled}
        x={NumUtils.metersToMmRounded(dimensions.centerX)}
        onXChange={onXChange}
        y={NumUtils.metersToMmRounded(dimensions.centerY)}
        onYChange={onYChange}
        length={NumUtils.metersToMmRounded(dimensions.length)}
        onLengthChange={onLengthChange}
        width={NumUtils.metersToMmRounded(dimensions.width)}
        onWidthChange={onWidthChange}
        angle={dimensions.angle}
        onAngleChange={onAngleChange}
      />
    </Accordion>
  );
}

export const LogicalSpaceDimensions = memo(LogicalSpaceDimensionsComponent);
